import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ExternalLink, X, Download } from "lucide-react";
import { downloadPdfUrl } from "@/lib/downloadBlob";

/*
 * PDFViewerModal — embeds a backend PDF endpoint inside an iframe so users
 * never leave the app. Used for Quote/Invoice, Vendor PO, and any other
 * document in the Parts Counter workflow. Includes a "Download" button
 * (triggers the native Save As dialog) and an "Open in new tab" escape
 * hatch for users who prefer their browser's native PDF tooling.
 */
export default function PDFViewerModal({ open, onClose, url, title = "Document" }) {
  if (!url) return null;

  // Derive the API path from the full URL so we can hand it to the helper.
  // Helper prefers an absolute Content-Disposition from the backend; we add
  // ?download=1 when the URL doesn't already have it.
  const handleDownload = () => {
    const apiBase = process.env.REACT_APP_BACKEND_URL || "";
    let path = url.startsWith(apiBase) ? url.slice(apiBase.length) : url;
    if (!path.includes("download=1")) {
      path += path.includes("?") ? "&download=1" : "?download=1";
    }
    downloadPdfUrl(path, { label: title, suggestedName: `${title}.pdf` });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-[92vw] h-[90vh] p-0 flex flex-col" data-testid="pdf-viewer-modal">
        <DialogHeader className="px-4 py-2.5 border-b flex flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-sm font-semibold text-slate-700">{title}</DialogTitle>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={handleDownload} data-testid="pdf-viewer-download">
              <Download className="w-3 h-3 mr-1" /> Download
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => window.open(url, "_blank")} data-testid="pdf-viewer-open-new-tab">
              <ExternalLink className="w-3 h-3 mr-1" /> Open in tab
            </Button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="pdf-viewer-close">
              <X className="w-4 h-4" />
            </button>
          </div>
        </DialogHeader>
        <iframe
          src={url}
          title={title}
          className="flex-1 w-full border-0"
          data-testid="pdf-viewer-iframe"
        />
      </DialogContent>
    </Dialog>
  );
}
